import { useEffect, useMemo, useState } from "react"
import kateAvatarSrc from "./assets/kate-avatar.png"
import clientAvatarSrc from "../../../../components/startups-landing/assets/client-avatar.png"
import chatStyles from "../../../../components/startups-landing/startups-landing-sections.module.css"
import styles from "./find-work.module.css"

/** Chat script: Alex (candidate) ↔ Kate */
const SCRIPT = [
	{
		from: "alex",
		text: "Hi Kate! I'm available for new roles starting next month.",
	},
	{
		from: "kate",
		text: "Great to hear, Alex! What kind of role are you targeting?",
	},
	{
		from: "alex",
		text: "Senior / Lead Developer at an early-stage Fintech startup.",
	},
	{
		from: "kate",
		text: "Got it. Full-time, or open to contract as well?",
	},
	{
		from: "alex",
		text: "Full-time only. Fully remote, or hybrid in Frankfurt/Berlin.",
	},
	{
		from: "kate",
		text: "Noted. And your target salary range?",
	},
	{
		from: "alex",
		text: "Around €110–130k gross. I'd flex for the right equity.",
	},
	{
		from: "kate",
		text:
			"Here's your search profile:\n\n" +
			"Role: Senior / Lead Developer\n" +
			"Domain: Fintech · Early-stage\n" +
			"Type: Full-time · Remote-first\n" +
			"Salary: €110–130k + equity\n" +
			"Start: Next month",
	},
	{
		from: "kate",
		text: "Shall I kick off the search now?",
	},
	{
		from: "alex",
		text: "Yes, go ahead!",
	},
]

const MAX_VISIBLE_MESSAGES = 5
/** During message phase, switch to a tighter recent window earlier to keep the demo compact. */
const EARLY_TAIL_START_MSG_INDEX = 6
const TAIL_MESSAGES_DURING_MSGS = 3
/** During search + results, only show this many recent lines so the results bubble fits without scrolling */
const TAIL_MESSAGES_FOR_RESULTS = 2
const MSG_STEP_MS = 2800
const AFTER_DIALOG_MS = 900
const SEARCHING_MS = 3600
const RESULTS_HOLD_MS = 7000
const RECYCLE_DELAY_MS = 1400

const LOOP_PHASE_MSGS = "msgs"
const LOOP_PHASE_SEARCH = "search"
const LOOP_PHASE_RESULTS = "results"

function KateResultsLead() {
	return (
		<>
			Here are <strong className={chatStyles.strongGold}>2 strong role matches</strong> in the Techcore Network:
		</>
	)
}

function bubbleClass(extra) {
	return [chatStyles.msgBubble, styles.kateChatBubble, extra].filter(Boolean).join(" ")
}

function KateHeaderFace() {
	return <img src={kateAvatarSrc} alt="" className={chatStyles.annaHeaderFace} draggable={false} />
}

function KateFaceSm() {
	return <img src={kateAvatarSrc} alt="" className={chatStyles.msgAnnaFace} draggable={false} />
}

function AlexFaceSm() {
	return <img src={clientAvatarSrc} alt="" className={chatStyles.msgUserFaceImg} draggable={false} />
}

/** Rounded-square company logo marks */
function NovaLedgerIcon() {
	return (
		<div className={styles.kateCoIcon} style={{ background: "linear-gradient(145deg, #1e3a8a 0%, #3b82f6 100%)" }}>
			<svg width="15" height="15" viewBox="0 0 15 15" fill="none">
				<path d="M3 12V3L8.5 12V3" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
				<path d="M8.5 3L12 12" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
			</svg>
		</div>
	)
}

function RiverbankIcon() {
	return (
		<div className={styles.kateCoIcon} style={{ background: "linear-gradient(145deg, #064e3b 0%, #10b981 100%)" }}>
			<svg width="15" height="15" viewBox="0 0 15 15" fill="none">
				<path d="M3 3V12M3 3H8a2.5 2.5 0 0 1 0 5H3" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
				<path d="M7.5 8L12 12" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
			</svg>
		</div>
	)
}

const JOB_MATCHES = [
	{
		icon: <NovaLedgerIcon />,
		title: "Senior Software Engineer",
		meta: "Nova Ledger · Fintech · Remote EU",
		match: "96%",
	},
	{
		icon: <RiverbankIcon />,
		title: "Lead Developer",
		meta: "Riverbank · Early-stage · Berlin hybrid",
		match: "92%",
	},
]

export default function KateFindWorkDemo() {
	// Start at 1 so the first message is immediately visible; avoids empty-frame flash on init/recycle
	const [msgCount, setMsgCount] = useState(1)
	const [loopPhase, setLoopPhase] = useState(LOOP_PHASE_MSGS)
	const [reducedMotion, setReducedMotion] = useState(false)

	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
		setReducedMotion(mq.matches)
		const onChange = () => setReducedMotion(mq.matches)
		mq.addEventListener("change", onChange)
		return () => mq.removeEventListener("change", onChange)
	}, [])

	const visibleScript = useMemo(() => {
		const slice = SCRIPT.slice(0, msgCount)
		const tailPhase = loopPhase === LOOP_PHASE_SEARCH || loopPhase === LOOP_PHASE_RESULTS
		let win =
			slice.length <= MAX_VISIBLE_MESSAGES ? slice : slice.slice(-MAX_VISIBLE_MESSAGES)
		if (!tailPhase && slice.length >= EARLY_TAIL_START_MSG_INDEX) {
			win = slice.slice(-TAIL_MESSAGES_DURING_MSGS)
		}
		if (tailPhase && slice.length >= TAIL_MESSAGES_FOR_RESULTS) {
			win = slice.slice(-TAIL_MESSAGES_FOR_RESULTS)
		}
		const start = Math.max(0, slice.length - win.length)
		return win.map((m, i) => ({ ...m, scriptIndex: start + i }))
	}, [msgCount, loopPhase])

	/* Message sequence */
	useEffect(() => {
		if (reducedMotion) return undefined
		if (loopPhase !== LOOP_PHASE_MSGS) return undefined
		if (msgCount >= SCRIPT.length) {
			const t = setTimeout(() => setLoopPhase(LOOP_PHASE_SEARCH), AFTER_DIALOG_MS)
			return () => clearTimeout(t)
		}
		const t = setTimeout(() => setMsgCount((c) => c + 1), MSG_STEP_MS)
		return () => clearTimeout(t)
	}, [reducedMotion, loopPhase, msgCount])

	/* Searching → results */
	useEffect(() => {
		if (reducedMotion) return undefined
		if (loopPhase !== LOOP_PHASE_SEARCH) return undefined
		const t = setTimeout(() => setLoopPhase(LOOP_PHASE_RESULTS), SEARCHING_MS)
		return () => clearTimeout(t)
	}, [reducedMotion, loopPhase])

	/* Results → recycle */
	useEffect(() => {
		if (reducedMotion) return undefined
		if (loopPhase !== LOOP_PHASE_RESULTS) return undefined
		const t = setTimeout(() => {
			setMsgCount(1) // reset to 1 so first message is visible immediately (no empty-frame flash)
			setLoopPhase(LOOP_PHASE_MSGS)
		}, RESULTS_HOLD_MS + RECYCLE_DELAY_MS)
		return () => clearTimeout(t)
	}, [reducedMotion, loopPhase])

	const messagesClass = `${chatStyles.annaMessagesKateClip} ${styles.kateMessagesSurface}`

	if (reducedMotion) {
		return (
			<div className={`${chatStyles.annaDemo} ${styles.kateDemoTall}`} aria-hidden="true">
				<div className={chatStyles.annaDemoHeader}>
					<KateHeaderFace />
					<div>
						<div className={chatStyles.annaDemoName}>Kate</div>
						<div className={`${chatStyles.annaDemoStatus} ${styles.kateStatusNoDot}`} aria-hidden="true">
							<span>Career ally</span>
						</div>
					</div>
				</div>
				<div className={messagesClass}>
					<div className={`${chatStyles.msg} ${chatStyles.msgUser}`}>
						<AlexFaceSm />
						<div className={bubbleClass()}>{SCRIPT[9].text}</div>
					</div>
					<div className={`${chatStyles.msg} ${chatStyles.msgAnna}`}>
						<KateFaceSm />
						<div className={bubbleClass()}>
							<KateResultsLead />
							<div className={chatStyles.annaResults}>
						{JOB_MATCHES.map((j) => (
								<div key={j.title} className={chatStyles.annaResultCard}>
									{j.icon}
									<div className={chatStyles.arcInfo}>
										<div className={chatStyles.arcName}>{j.title}</div>
										<div className={chatStyles.arcRole}>{j.meta}</div>
									</div>
									<div className={chatStyles.arcScore}>{j.match}</div>
								</div>
							))}
						</div>
							Want me to request introductions for your top two?
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={`${chatStyles.annaDemo} ${styles.kateDemoTall}`} aria-hidden="true">
			<div className={chatStyles.annaDemoHeader}>
				<KateHeaderFace />
				<div>
					<div className={chatStyles.annaDemoName}>Kate</div>
					<div
						className={`${chatStyles.annaDemoStatus} ${styles.kateStatusNoDot}`}
						aria-hidden="true"
					>
						<span>Career ally</span>
					</div>
				</div>
			</div>

			<div className={messagesClass}>
				{visibleScript.map((m) => (
					<div
						key={`script-${m.scriptIndex}`}
						className={`${chatStyles.msg} ${m.from === "kate" ? chatStyles.msgAnna : chatStyles.msgUser}`}
					>
						{m.from === "kate" ? <KateFaceSm /> : <AlexFaceSm />}
						<div className={bubbleClass(m.text.includes("\n") ? styles.bubblePreline : "")}>{m.text}</div>
					</div>
				))}

				{loopPhase === LOOP_PHASE_SEARCH ? (
					<div className={chatStyles.annaSearchingBlock}>
						<KateFaceSm />
						<div className={chatStyles.annaSearchingBody}>
							<div className={chatStyles.annaSearchingTitle}>Searching Techcore Network</div>
							<div className={chatStyles.typingDots} aria-hidden>
								<span />
								<span />
								<span />
							</div>
						</div>
					</div>
				) : null}

				{loopPhase === LOOP_PHASE_RESULTS ? (
					<div className={`${chatStyles.msg} ${chatStyles.msgAnna}`}>
						<KateFaceSm />
						<div className={bubbleClass()}>
							<KateResultsLead />
							<div className={chatStyles.annaResults}>
							{JOB_MATCHES.map((j) => (
								<div key={j.title} className={chatStyles.annaResultCard}>
									{j.icon}
									<div className={chatStyles.arcInfo}>
										<div className={chatStyles.arcName}>{j.title}</div>
										<div className={chatStyles.arcRole}>{j.meta}</div>
									</div>
									<div className={chatStyles.arcScore}>{j.match}</div>
								</div>
							))}
							</div>
							Want me to request introductions for your top two?
						</div>
					</div>
				) : null}
			</div>
		</div>
	)
}
